import { generatePath } from 'react-router';
import { observer } from 'mobx-react';
import { useToggle } from '@cutwise/react-utils';

import FancyPopup from '@/common/components/FancyPopup/FancyPopup';
import CompareNavButton from '@/common/components/Header/PinNavItem/CompareNavButton/CompareNavButton';
import PinMenuListItem from '@/common/components/Header/PinNavItem/PinMenuListItem/PinMenuListItem';
import PinNavButton from '@/common/components/Header/PinNavItem/PinNavButton/PinNavButton';
import Icon from '@/common/components/Icon/Icon';
import Link from '@/common/components/Link/Link';
import { MenuProvider } from '@/common/components/MenuList/MenuContext/MenuContext';
import MenuItemSeparator from '@/common/components/MenuList/MenuItemSeparator/MenuItemSeparator';
import { DRIVE_PAGE } from '@/common/constants/pages';
import { DriveSection } from '@/drive/types/DriveSection';
import ForwardModal from '@/forwarding/components/ForwardModal/ForwardModal';
import { usePinItemsActions } from '@/forwarding/hooks/usePinItemsActions';

import MenuList from '../../MenuList/MenuList';

import styles from './PinNavItem.module.css';

function PinNavItem() {
  const {
    pinnedItems,
    unpinAll,
    productTypeAndProductsPairs,
    forwardConfig,
    openForward,
    closeForward,
    compareProducts,
    addProductsToCollection,
    formatCollectionLabel,
  } = usePinItemsActions();
  const [isOpened, openMenu, closeMenu] = useToggle();

  function onChangeMenuState(isMenuOpened: boolean) {
    if (isMenuOpened) {
      openMenu();
    } else {
      closeMenu();
    }
  }

  if (pinnedItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.pinNavItem}>
      <ForwardModal config={forwardConfig} onClose={closeForward} />

      <Link
        aria-label="Pinned items"
        className={styles.textContainer}
        to={generatePath(DRIVE_PAGE, { section: DriveSection.PinnedItems })}
      >
        <Icon className={styles.pinIcon} name="pin-filled" />

        {pinnedItems.length}
      </Link>

      <CompareNavButton productTypeAndProductsPairs={productTypeAndProductsPairs} onSelect={compareProducts} />

      <PinNavButton icon="share-outline" aria-label="Forward pinned items" onClick={() => openForward(pinnedItems)} />

      <MenuProvider onClick={closeMenu}>
        <FancyPopup
          isOpen={isOpened}
          onChangeState={onChangeMenuState}
          onClickOutside={closeMenu}
          className={styles.pinPopup}
          action="click"
          trigger={<PinNavButton className={styles.moreButton} icon="more" aria-label="More pinned actions" />}
          body={(
            <MenuList>
              {productTypeAndProductsPairs.map(([productType, products]) => (
                <PinMenuListItem
                  key={`collection-${productType}`}
                  aria-label="Add pinned to collection"
                  icon="like"
                  onClick={() => addProductsToCollection(products)}
                >
                  {formatCollectionLabel(productType, products.length)}
                </PinMenuListItem>
              ))}

              {productTypeAndProductsPairs.length > 0 && <MenuItemSeparator />}

              <PinMenuListItem aria-label="Unpin all" icon="delete" onClick={unpinAll}>
                Unpin All
              </PinMenuListItem>
            </MenuList>
          )}
        />
      </MenuProvider>
    </div>
  );
}

export default observer(PinNavItem);
